import { api_service } from './api';

/**
 * Settings Service
 * Handles all settings-related API operations
 */

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface CompanySettings {
  id?: number;
  company_name: string;
  company_email: string;
  company_phone: string;
  company_address: string;
  tax_id: string;
  bank_name?: string;
  account_name?: string;
  account_number?: string;
}

export interface BillingSettings {
  id?: number;
  default_tax_rate: number;
  payment_terms: string;
  invoice_prefix: string;
  late_fee_enabled: boolean;
  late_fee_percentage: number;
}

export interface NotificationSettings {
  id?: number;
  email_notifications: boolean;
  sms_notifications: boolean;
  overdue_reminders: boolean;
  payment_confirmations: boolean;
}

export interface AccountSettings {
  id?: number;
  full_name: string;
  email: string;
  phone: string;
}

export interface PasswordChangeData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface SystemInfo {
  version: string;
  database: string;
  last_backup: string;
  storage_used: string;
  storage_total: string;
  uptime?: string;
}

export interface AllSettings {
  company: CompanySettings;
  billing: BillingSettings;
  notifications: NotificationSettings;
  account: AccountSettings;
  system: SystemInfo;
}

// ============================================================================
// Settings Service Class
// ============================================================================

class SettingsService {
  private readonly BASE_URL = '/settings';

  // --------------------------------------------------------------------------
  // Company Settings
  // --------------------------------------------------------------------------

  /**
   * Get company settings
   */
  async get_company_settings(): Promise<CompanySettings> {
    try {
      return await api_service.get<CompanySettings>(`${this.BASE_URL}/company/`);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
      throw error;
    }
  }

  /**
   * Update company settings
   */
  async update_company_settings(data: Partial<CompanySettings>): Promise<CompanySettings> {
    try {
      return await api_service.patch<CompanySettings>(`${this.BASE_URL}/company/`, data);
    } catch (error) {
      console.error('Failed to update company settings:', error);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // Billing Settings
  // --------------------------------------------------------------------------

  /**
   * Get billing settings
   */
  async get_billing_settings(): Promise<BillingSettings> {
    try {
      return await api_service.get<BillingSettings>(`${this.BASE_URL}/billing/`);
    } catch (error) {
      console.error('Failed to fetch billing settings:', error);
      throw error;
    }
  }

  /**
   * Update billing settings
   */
  async update_billing_settings(data: Partial<BillingSettings>): Promise<BillingSettings> {
    try {
      return await api_service.patch<BillingSettings>(`${this.BASE_URL}/billing/`, data);
    } catch (error) {
      console.error('Failed to update billing settings:', error);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // Notification Settings
  // --------------------------------------------------------------------------

  /**
   * Get notification settings
   */
  async get_notification_settings(): Promise<NotificationSettings> {
    try {
      return await api_service.get<NotificationSettings>(`${this.BASE_URL}/notifications/`);
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
      throw error;
    }
  }

  /**
   * Update notification settings
   */
  async update_notification_settings(data: Partial<NotificationSettings>): Promise<NotificationSettings> {
    try {
      return await api_service.patch<NotificationSettings>(`${this.BASE_URL}/notifications/`, data);
    } catch (error) {
      console.error('Failed to update notification settings:', error);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // Account Settings
  // --------------------------------------------------------------------------

  /**
   * Get account settings
   */
  async get_account_settings(): Promise<AccountSettings> {
    try {
      return await api_service.get<AccountSettings>(`${this.BASE_URL}/account/`);
    } catch (error) {
      console.error('Failed to fetch account settings:', error);
      throw error;
    }
  }

  /**
   * Update account settings
   */
  async update_account_settings(data: Partial<AccountSettings>): Promise<AccountSettings> {
    try {
      return await api_service.patch<AccountSettings>(`${this.BASE_URL}/account/`, data);
    } catch (error) {
      console.error('Failed to update account settings:', error);
      throw error;
    }
  }

  /**
   * Change password
   */
  async change_password(data: PasswordChangeData): Promise<{ message: string }> {
    try {
      // Validate passwords match
      if (data.new_password !== data.confirm_password) {
        throw new Error('New passwords do not match');
      }

      return await api_service.post<{ message: string }>(
        `${this.BASE_URL}/account/change-password/`,
        {
          current_password: data.current_password,
          new_password: data.new_password,
        }
      );
    } catch (error) {
      console.error('Failed to change password:', error);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // System Settings
  // --------------------------------------------------------------------------

  /**
   * Get system information
   */
  async get_system_info(): Promise<SystemInfo> {
    try {
      return await api_service.get<SystemInfo>(`${this.BASE_URL}/system/`);
    } catch (error) {
      console.error('Failed to fetch system info:', error);
      throw error;
    }
  }

  /**
   * Backup database
   */
  async backup_database(): Promise<{ message: string; filename: string }> {
    try {
      return await api_service.post<{ message: string; filename: string }>(
        `${this.BASE_URL}/system/backup/`
      );
    } catch (error) {
      console.error('Failed to backup database:', error);
      throw error;
    }
  }

  /**
   * Clear cache
   */
  async clear_cache(): Promise<{ message: string }> {
    try {
      return await api_service.post<{ message: string }>(
        `${this.BASE_URL}/system/clear-cache/`
      );
    } catch (error) {
      console.error('Failed to clear cache:', error);
      throw error;
    }
  }

  /**
   * Reset system (admin only)
   */
  async reset_system(confirmation: string): Promise<{ message: string }> {
    try {
      if (confirmation !== 'RESET') {
        throw new Error('Invalid confirmation');
      }

      return await api_service.post<{ message: string }>(
        `${this.BASE_URL}/system/reset/`,
        { confirmation }
      );
    } catch (error) {
      console.error('Failed to reset system:', error);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // Bulk Operations
  // --------------------------------------------------------------------------

  /**
   * Get all settings at once
   */
  async get_all_settings(): Promise<AllSettings> {
    try {
      return await api_service.get<AllSettings>(`${this.BASE_URL}/all/`);
    } catch (error) {
      console.error('Failed to fetch all settings:', error);
      throw error;
    }
  }

  /**
   * Update multiple settings at once
   */
  async update_all_settings(data: {
    company?: Partial<CompanySettings>;
    billing?: Partial<BillingSettings>;
    notifications?: Partial<NotificationSettings>;
    account?: Partial<AccountSettings>;
  }): Promise<AllSettings> {
    try {
      return await api_service.patch<AllSettings>(`${this.BASE_URL}/all/`, data);
    } catch (error) {
      console.error('Failed to update settings:', error);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // Helper Methods
  // --------------------------------------------------------------------------

  /**
   * Validate company settings before submission
   */
  validate_company_settings(data: Partial<CompanySettings>): string[] {
    const errors: string[] = [];

    if (data.company_name !== undefined && data.company_name.trim().length < 2) {
      errors.push('Company name must be at least 2 characters');
    }

    if (data.company_email !== undefined && !this.is_valid_email(data.company_email)) {
      errors.push('Invalid email address');
    }

    if (data.company_phone !== undefined && data.company_phone.trim().length < 10) {
      errors.push('Invalid phone number');
    }

    return errors;
  }

  /**
   * Validate billing settings before submission
   */
  validate_billing_settings(data: Partial<BillingSettings>): string[] {
    const errors: string[] = [];

    if (data.default_tax_rate !== undefined) {
      if (data.default_tax_rate < 0 || data.default_tax_rate > 100) {
        errors.push('Tax rate must be between 0 and 100');
      }
    }

    if (data.late_fee_percentage !== undefined) {
      if (data.late_fee_percentage < 0 || data.late_fee_percentage > 100) {
        errors.push('Late fee percentage must be between 0 and 100');
      }
    }

    if (data.invoice_prefix !== undefined && data.invoice_prefix.trim().length === 0) {
      errors.push('Invoice prefix cannot be empty');
    }

    return errors;
  }

  /**
   * Validate password change data
   */
  validate_password_change(data: PasswordChangeData): string[] {
    const errors: string[] = [];

    if (!data.current_password) {
      errors.push('Current password is required');
    }

    if (!data.new_password) {
      errors.push('New password is required');
    } else if (data.new_password.length < 8) {
      errors.push('New password must be at least 8 characters');
    }

    if (data.new_password !== data.confirm_password) {
      errors.push('Passwords do not match');
    }

    if (data.current_password === data.new_password) {
      errors.push('New password must be different from current password');
    }

    return errors;
  }

  /**
   * Simple email validation
   */
  private is_valid_email(email: string): boolean {
    const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email_regex.test(email);
  }
}

// Export singleton instance
export const settings_service = new SettingsService();